import { Link } from "react-router-dom";
import { useCategories } from "@/hooks/useExam";

const CATEGORY_ICONS: Record<string, string> = {
  alertness: "M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z",
  attitude: "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z",
  default: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
};

export default function Categories() {
  const { data: categories, isLoading, error } = useCategories();

  return (
    <div className="page-enter mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Question Categories</h1>
        <p className="mt-2 text-gray-500">
          Browse questions by category. Focus on areas where you need the most practice.
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <svg className="animate-spin h-8 w-8 text-primary-600" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-danger-50 border border-danger-200 p-6 text-center">
          <p className="text-danger-700">Failed to load categories. Please try again later.</p>
        </div>
      )}

      {categories && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => (
            <Link
              key={category.id}
              to={`/categories/${category.slug}`}
              className="block rounded-xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md hover:border-primary-300 transition-all group"
            >
              <div className="flex items-start space-x-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d={CATEGORY_ICONS[category.slug] || CATEGORY_ICONS.default}
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                    {category.name}
                  </h3>
                  {category.description && (
                    <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                      {category.description}
                    </p>
                  )}
                  <p className="mt-2 text-sm font-medium text-primary-600">
                    {category.question_count} questions
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {categories && categories.length === 0 && (
        <div className="text-center py-20">
          <p className="text-gray-500">No categories available yet.</p>
        </div>
      )}
    </div>
  );
}
